import React, { useState, useEffect, useMemo } from "react";

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link,
    Box,
    Chip,
    FormControlLabel,
    Switch,
    Button,
} from "@mui/material";

function CmsSearchResults({
    results = [],
    loading = false,
    hasMore = false,
    handleLoadMore,
}) {

    const [hasStarted, setHasStarted] =
        useState(false);

    // Filters
    const [onlyTypo3, setOnlyTypo3] =
        useState(false);

    const [onlyPhp7, setOnlyPhp7] =
        useState(false);

    useEffect(() => {

        if (loading) {
            setHasStarted(true);
        }

    }, [loading]);

    const hasResults =
        results &&
        results.length > 0;

    const filteredResults = useMemo(() => {

        return (results || []).filter(result => {

            const cmsList = result.cms || [];

            // TYPO3 filter
            if (onlyTypo3) {

                const hasTypo3 =
                    cmsList.some(tech =>
                        (tech.name || "")
                            .toLowerCase()
                            .includes("typo3")
                    );

                if (!hasTypo3) {
                    return false;
                }
            }

            // PHP 7 filter
            if (onlyPhp7) {

                const hasPhp7 =
                    cmsList.some(tech => {

                        const isPhp =
                            (tech.name || "")
                                .toLowerCase()
                                .includes("php");

                        const hasVersion7 =
                            (tech.versions || [])
                                .some(version =>
                                    version.startsWith("7.")
                                );

                        return (
                            isPhp &&
                            hasVersion7
                        );
                    });

                if (!hasPhp7) {
                    return false;
                }
            }

            return true;

        });

    }, [
        results,
        onlyTypo3,
        onlyPhp7,
    ]);

    if (
        !hasStarted &&
        !loading &&
        !hasResults
    ) {
        return null;
    }

    const TypingRow = () => (
        <TableRow>

            {Array.from({ length: 5 }).map(
                (_, i) => (

                    <TableCell key={i}>

                        <Box
                            sx={{
                                width: "100%",
                                height: 18,
                                borderRadius: 1,
                                bgcolor:
                                    "rgba(0,0,0,0.08)",
                                overflow: "hidden",
                                position: "relative",

                                "@keyframes shimmer": {
                                    "0%": {
                                        left: "-100%",
                                    },
                                    "100%": {
                                        left: "100%",
                                    },
                                },

                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "50%",
                                    height: "100%",
                                    background:
                                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                                    animation:
                                        "shimmer 1.8s infinite",
                                },
                            }}
                        />

                    </TableCell>
                )
            )}

        </TableRow>
    );

    return (

        <TableContainer
            component={Paper}
            sx={{
                mt: 2,
                width: "100%",
                overflowX: "auto",
            }}
        >

            {/* Filters */}

            <Box
                sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >

                <Box
                    sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                    }}
                >
                    Legacy Homepages
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >

                    <FormControlLabel
                        control={
                            <Switch
                                checked={onlyTypo3}
                                onChange={(e) =>
                                    setOnlyTypo3(
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label="Nur TYPO3"
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={onlyPhp7}
                                onChange={(e) =>
                                    setOnlyPhp7(
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label="Nur PHP 7.x"
                    />

                </Box>

            </Box>

            <Table size="small">

                <TableHead>

                    <TableRow>

                        <TableCell>
                            Titel
                        </TableCell>

                        <TableCell>
                            URL
                        </TableCell>

                        <TableCell>
                            Technologien
                        </TableCell>

                        <TableCell>
                            Treffer
                        </TableCell>

                        <TableCell>
                            Snippet
                        </TableCell>

                    </TableRow>

                </TableHead>

                <TableBody>

                    {filteredResults.map(
                        (result, index) => (

                            <TableRow key={index}>

                                <TableCell
                                    sx={{
                                        wordBreak:
                                            "break-word",
                                    }}
                                >

                                    {result.title || "—"}

                                </TableCell>

                                <TableCell
                                    sx={{
                                        wordBreak:
                                            "break-all",
                                        maxWidth: 320,
                                    }}
                                >

                                    {result.link ? (

                                        <Link
                                            href={result.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                        >

                                            {result.link}

                                        </Link>

                                    ) : "—"}

                                </TableCell>

                                <TableCell
                                    sx={{
                                        minWidth: 320,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            gap: 1,
                                            flexWrap:
                                                "wrap",
                                        }}
                                    >

                                        {(result.cms || [])
                                            .length > 0 ? (

                                            result.cms.map(
                                                (
                                                    tech,
                                                    techIndex
                                                ) => (

                                                    <Chip
                                                        key={
                                                            techIndex
                                                        }
                                                        label={
                                                            tech.versions &&
                                                            tech.versions.length > 0
                                                                ? `${tech.name} ${tech.versions.join(", ")}`
                                                                : tech.name
                                                        }
                                                        size="small"
                                                        variant="outlined"
                                                    />

                                                )
                                            )

                                        ) : (

                                            <Chip
                                                label="Nichts erkannt"
                                                size="small"
                                                color="default"
                                            />

                                        )}

                                    </Box>

                                </TableCell>

                                <TableCell>

                                    {result.technology_count || 0}

                                </TableCell>

                                <TableCell
                                    sx={{
                                        whiteSpace:
                                            "normal",
                                        wordBreak:
                                            "break-word",
                                        maxWidth: 450,
                                    }}
                                >

                                    {result.snippet || "—"}

                                </TableCell>

                            </TableRow>

                        )
                    )}

                    {loading && <TypingRow />}

                </TableBody>

            </Table>

            {hasMore && !loading && (

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                        mb: 2,
                    }}
                >

                    <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                    >
                        Weitere Ergebnisse laden
                    </Button>

                </Box>

            )}

        </TableContainer>
    );
}

export default CmsSearchResults;